import React, { useCallback, useEffect, useState } from 'react';
import { Plus, LayoutTemplate, Trash2, Pencil } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/crm/EmptyState';
import Modal from '../../components/admin/Modal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import TemplateEditor from '../../components/admin/TemplateEditor';
import { PURPOSE_COPY } from '../../lib/broadcasts';
import { relativeTime, firstName } from '../../lib/communications';

const inputCls = 'w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white placeholder-gray-500 focus:outline-none focus:border-[#B0FC38]/40';

// Communications → Templates: saved messages with {{tokens}}, reusable from
// the broadcast editor and the composer.
export default function TemplatesTab({ onToast }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | template
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try { setRows((await api.get('/api/admin/communications/templates'))?.templates || []); }
    catch (err) { setError(err?.message || 'Failed to load templates'); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    await api.delete(`/api/admin/communications/templates/${t.id}`);
    onToast?.({ message: `Deleted “${t.name}”` });
    load();
  };

  return (
    <div data-testid="templates-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <p className="text-[13px] text-gray-400 max-w-[60ch]">
          Write a message once — “Monthly check-in”, “Your site is live” — and reuse it in broadcasts and emails. Tokens like <code className="text-gray-300">{'{{school_name}}'}</code> fill in per school.
        </p>
        <button type="button" onClick={() => setEditing('new')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900" data-testid="new-template">
          <Plus className="w-3 h-3" aria-hidden="true" /> New template
        </button>
      </div>

      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">{error}</div>}

      {rows === null ? (
        <div className="space-y-2 animate-pulse">{[0, 1].map((i) => <div key={i} className="h-14 rounded-xl bg-white/[0.03]" />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={LayoutTemplate} title="No templates yet" description="Save your first one and it appears under “Insert template” in broadcasts and the composer." />
      ) : (
        <ul className="space-y-2 list-none m-0 p-0">
          {rows.map((t) => (
            <li key={t.id} className="p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] flex items-center justify-between gap-3" data-testid="template-row">
              <button type="button" onClick={() => setEditing(t)} className="min-w-0 text-left flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[14px] text-white font-medium truncate">{t.name}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border border-white/[0.08] text-gray-400 shrink-0">{PURPOSE_COPY[t.purpose]?.label || t.purpose}</span>
                </div>
                <div className="text-[12px] text-gray-500 mt-0.5 truncate">{t.subjectTemplate || <span className="italic">No subject</span>}</div>
                <div className="text-[11.5px] text-gray-600 mt-1">{t.createdByName ? `by ${firstName(t.createdByName)} · ` : ''}updated {relativeTime(t.updatedAt)}</div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => setEditing(t)} aria-label={`Edit ${t.name}`} className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]"><Pencil className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => setPendingDelete(t)} aria-label={`Delete ${t.name}`} className="p-2 rounded-lg text-gray-500 hover:text-red-300 hover:bg-white/[0.05]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <TemplateModal
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(t, created) => { setEditing(null); onToast?.({ message: created ? `Saved “${t.name}”` : `Updated “${t.name}”` }); load(); }}
        />
      )}
      <ConfirmDialog
        open={!!pendingDelete}
        title={pendingDelete ? `Delete “${pendingDelete.name}”?` : ''}
        description="Broadcasts already sent from it are unaffected. This can’t be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => remove(pendingDelete)}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

export function TemplateModal({ template, onClose, onSaved }) {
  const { user } = useAuth();
  const [name, setName] = useState(template?.name || '');
  const [purpose, setPurpose] = useState(template?.purpose || 'service');
  const [subject, setSubject] = useState(template?.subjectTemplate || '');
  const [bodyHtml, setBodyHtml] = useState(template?.bodyTemplateHtml || '');
  const [bodyText, setBodyText] = useState(template?.bodyTemplateText || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!name.trim()) { setError('Give the template a name.'); return; }
    setSaving(true); setError('');
    const payload = { name: name.trim(), purpose, subject_template: subject, body_template_html: bodyHtml, body_template_text: bodyText };
    try {
      const d = template
        ? await api.patch(`/api/admin/communications/templates/${template.id}`, payload)
        : await api.post('/api/admin/communications/templates', payload);
      onSaved?.(d.template, !template);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Could not save the template');
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={template ? `Edit · ${template.name}` : 'New template'} maxWidth="max-w-5xl"
      footer={
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {error ? <span className="text-[12px] text-red-300" role="alert">{error}</span> : <span className="text-[11.5px] text-gray-500">Tokens are filled from each school when the message is sent.</span>}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white">Cancel</button>
            <button type="button" onClick={save} disabled={saving} className="px-4 py-1.5 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-60" data-testid="template-save">
              {saving ? 'Saving…' : (template ? 'Save changes' : 'Save template')}
            </button>
          </div>
        </div>
      }>
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_260px] gap-3 mb-4">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Name</span>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Monthly check-in" className={inputCls} data-testid="template-name" />
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Purpose</span>
          <select value={purpose} onChange={(e) => setPurpose(e.target.value)} className={inputCls} data-testid="template-purpose">
            {Object.entries(PURPOSE_COPY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
      </div>
      <TemplateEditor
        subject={subject} onSubjectChange={setSubject}
        bodyHtml={bodyHtml} onBodyHtmlChange={setBodyHtml}
        bodyText={bodyText} onBodyTextChange={setBodyText}
        sender={{ name: user?.name, email: user?.email }}
      />
    </Modal>
  );
}
